import { useEffect, useState, useMemo } from "react";
import { SlidingNumber } from "@/components/ui/sliding-number";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { fetchProductByHandle, fetchProductsByCollection, type ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/store/cartStore";
import { useWishlistStore } from "@/store/wishlistStore";
import { alertToast } from "@/contexts/AlertToastContext";
import { Minus, Plus, ChevronRight, Heart, Share2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import ShopifyProductCard from "@/components/ShopifyProductCard";
import { motion, AnimatePresence } from "motion/react";
import { formatPrice } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getColorHexSync } from "@/lib/colorService";
import { getJewelryGradient } from "@/lib/colorGradient";
import { ProductFAQ } from "@/components/ProductFAQ";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { ImageMagnifier } from "@/components/ImageMagnifier";
import { 
  EngravingOptions, 
  ENGRAVING_PRICE, 
  getEngravingCount, 
  validateEngraving,
  type EngravingData 
} from "@/components/EngravingOptions";
import { IconSelection, hasIconTag, ICON_PRICE, type IconData } from "@/components/IconSelection";
import { RingSizeGuideDialog, hasRingSizeGuideTag } from "@/components/RingSizeGuideDialog";
import { SEO, SITE_URL } from "@/components/SEO";
import DOMPurify from "dompurify";

const isColorOptionName = (name: string) => {
  const n = name.toLowerCase();
  return (
    n === 'צבע' ||
    n === 'color' ||
    n === 'colour' ||
    n.includes('צבע') ||
    n.includes('color') ||
    n.includes('colour') ||
    n === 'גוון' ||
    n === 'shade' ||
    n === 'metal' ||
    n === 'מתכת'
  );
};

const Product = () => {
  const { handle, collection } = useParams<{ handle: string; collection?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState<ShopifyProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState<Array<{ node: ShopifyProduct }>>([]);
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<ShopifyProduct[]>([]);
  const [showHeartParticles, setShowHeartParticles] = useState(false);
  const [activeProductsTab, setActiveProductsTab] = useState<'related' | 'recent'>('related');
  const [engravingData, setEngravingData] = useState<EngravingData | null>(null);
  const [engravingErrors, setEngravingErrors] = useState<{ font?: string; texts?: string[] }>({});
  const [iconData, setIconData] = useState<IconData | null>(null);
  const [noEngraving, setNoEngraving] = useState(false);
  const addItem = useCartStore((state) => state.addItem);
  const { toggleItem, isInWishlist } = useWishlistStore();
  const { addProductHandle, getRecentlyViewedHandles } = useRecentlyViewed();
  
  const isLiked = product ? isInWishlist(product.id) : false;
  const recentlyViewedHandles = getRecentlyViewedHandles(handle);
  
  // Check if product has "מילים" (words) option for auto variant selection
  const wordsOption = product?.options?.find(opt => 
    opt.name === 'מילים' || opt.name.toLowerCase() === 'words'
  );
  const hasWordsOption = !!wordsOption;

  // Get referrer category from location state
  const referrerCategory = location.state?.fromCategory as { id: string; title: string } | undefined;

  // Get color and variant from URL query params
  const colorFromUrl = searchParams.get('color');
  const variantFromUrl = searchParams.get('variant');

  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;
      
      setIsLoading(true);
      const data = await fetchProductByHandle(handle);
      setProduct(data);
      
      if (data && data.variants?.edges?.length > 0) {
        // Check if there's a variant ID in the URL (Shopify format)
        let targetVariant = data.variants.edges[0].node;
        const initialOptions: Record<string, string> = {};
        
        if (variantFromUrl) {
          // Find variant that matches the variant ID from URL
          // The URL contains just the numeric ID, but Shopify stores it as gid://shopify/ProductVariant/ID
          const variantMatch = data.variants.edges.find((edge) =>
            edge.node.id.includes(`ProductVariant/${variantFromUrl}`)
          );
          
          if (variantMatch) {
            targetVariant = variantMatch.node;
          }
        } else if (colorFromUrl) {
          // Find variant that matches the color from URL
          const colorVariant = data.variants.edges.find((edge) =>
            edge.node.selectedOptions?.some((opt) => 
              isColorOptionName(opt.name) && opt.value === colorFromUrl
            )
          );
          
          if (colorVariant) {
            targetVariant = colorVariant.node;
          }
        }
        
        // Initialize selected options from target variant
        targetVariant.selectedOptions.forEach((option) => {
          initialOptions[option.name] = option.value;
        });
        
        setSelectedVariant(targetVariant);
        setSelectedOptions(initialOptions);
        
        // If color variant has an image, sync the gallery
        if (colorFromUrl && targetVariant.image?.url) {
          const getBaseUrl = (url: string) => url.split('?')[0];
          const variantBaseUrl = getBaseUrl(targetVariant.image.url);
          
          const imageIndex = data.images?.edges?.findIndex((img) => 
            getBaseUrl(img.node.url) === variantBaseUrl
          ) ?? -1;
          
          if (imageIndex !== -1) {
            setSelectedImage(imageIndex);
          }
        }
      }
      
      setIsLoading(false);
    };

    loadProduct();
  }, [handle, colorFromUrl]);

  useEffect(() => {
    const loadRelatedProducts = async () => {
      try {
        // Get the first collection from product's collections
        const productCollections = product?.collections?.edges;
        
        if (productCollections && productCollections.length > 0) {
          // Fetch products from the same collection
          const collectionHandle = productCollections[0].node.handle;
          const { edges } = await fetchProductsByCollection(collectionHandle, 8);
          // Filter out current product
          const filtered = edges.filter(p => p.node.handle !== handle);
          setRelatedProducts(filtered.slice(0, 4).map(p => ({ node: p.node })));
        }
      } catch (error) {
        console.error('Error loading related products:', error);
      }
    };

    if (product) {
      loadRelatedProducts();
      
      // Add to recently viewed (just the handle)
      addProductHandle(product.handle);
    }
  }, [product, handle]);

  // Fetch full product data for recently viewed handles
  useEffect(() => {
    const fetchRecentlyViewedProducts = async () => {
      if (recentlyViewedHandles.length === 0) {
        setRecentlyViewedProducts([]);
        return;
      }
      
      try {
        const products = await Promise.all(
          recentlyViewedHandles.map(h => fetchProductByHandle(h))
        );
        // Filter out null results and current product
        const validProducts = products.filter((p): p is ShopifyProduct => p !== null && p.handle !== handle);
        setRecentlyViewedProducts(validProducts);
      } catch (error) {
        console.error('Error fetching recently viewed products:', error);
      }
    };

    fetchRecentlyViewedProducts();
  }, [recentlyViewedHandles, handle]);

  const handleOptionChange = (optionName: string, value: string) => {
    const newOptions = { ...selectedOptions, [optionName]: value };
    setSelectedOptions(newOptions);

    // Find matching variant
    const variant = product?.variants.edges.find((edge) => {
      return edge.node.selectedOptions.every(
        (option) => newOptions[option.name] === option.value
      );
    });

    if (variant) {
      setSelectedVariant(variant.node);
      // If this is a color option and the selected variant has an image, sync the gallery
      if (isColorOptionName(optionName)) {
        const variantImageUrl = variant.node.image?.url;
        if (variantImageUrl) {
          // Extract base URL without query params for comparison
          const getBaseUrl = (url: string) => url.split('?')[0];
          const variantBaseUrl = getBaseUrl(variantImageUrl);
          
          const imageIndex = product?.images?.edges?.findIndex((img) => 
            getBaseUrl(img.node.url) === variantBaseUrl
          ) ?? -1;
          
          if (imageIndex !== -1) {
            setSelectedImage(imageIndex);
          }
        }
      }
    }
  };

  // Count words in all engraving texts
  const getTotalWordCount = (): number => {
    if (!engravingData?.texts) return 0;
    return engravingData.texts.reduce((total, text) => {
      const words = text.trim().split(/\s+/).filter(Boolean);
      return total + words.length;
    }, 0);
  };

  // Auto-select "מילים" variant based on word count or noEngraving checkbox
  useEffect(() => {
    if (!hasWordsOption || !product) return;
    
    let targetValue: string;
    if (noEngraving) {
      targetValue = '0';
    } else {
      const wordCount = getTotalWordCount();
      // Map word count to variant value: 0, 1, 2, or 3
      targetValue = Math.min(wordCount, 3).toString();
    }
    
    // Only update if different from current selection
    const wordsOptionName = wordsOption?.name || 'מילים';
    if (selectedOptions[wordsOptionName] !== targetValue) {
      // Update options directly without calling handleOptionChange to avoid loops
      const newOptions = { ...selectedOptions, [wordsOptionName]: targetValue };
      setSelectedOptions(newOptions);

      // Find matching variant
      const variant = product?.variants.edges.find((edge) => {
        return edge.node.selectedOptions.every(
          (option) => newOptions[option.name] === option.value
        );
      });

      if (variant) {
        setSelectedVariant(variant.node);
      }
    }
  }, [engravingData, noEngraving, hasWordsOption, product, wordsOption?.name]);

  // Check if this product requires engraving and how many fields
  const engravingCount = getEngravingCount(product?.tags);
  const requiresEngraving = engravingCount > 0;
  const hasIcons = hasIconTag(product?.tags);

  const handleAddToCart = () => {
    if (!product || !selectedVariant) return;

    // Validate engraving if required and not disabled
    if (requiresEngraving && !noEngraving) {
      const errors = validateEngraving(engravingData, engravingCount);
      if (Object.keys(errors).length > 0) {
        setEngravingErrors(errors);
        alertToast.error("יש למלא את פרטי החריטה", "בחר פונט והזן טקסט לחריטה");
        return;
      }
      setEngravingErrors({});
    }

    const cartItem = {
      product: {
        id: product.id,
        title: product.title,
        description: product.description,
        handle: product.handle,
        priceRange: product.priceRange,
        images: product.images,
        variants: product.variants,
        options: product.options,
      },
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: {
        amount: selectedVariant.price.amount,
        currencyCode: selectedVariant.price.currencyCode,
      },
      quantity,
      selectedOptions: Object.entries(selectedOptions).map(([name, value]) => ({
        name,
        value,
      })),
      // Include variant image if available
      variantImage: selectedVariant.image ? {
        url: selectedVariant.image.url,
        altText: selectedVariant.image.altText,
      } : undefined,
      // Include engraving data if applicable (only when not "no engraving")
      engraving: requiresEngraving && engravingData && !noEngraving ? {
        font: engravingData.font,
        texts: engravingData.texts,
        price: ENGRAVING_PRICE,
      } : undefined,
      // Include icon data if applicable
      icons: hasIcons && iconData && iconData.iconIds.length > 0 ? iconData.iconIds : undefined,
    };

    addItem(cartItem);
    
    // Remove from wishlist if it's there
    if (isLiked) {
      toggleItem(product);
    }
    
    alertToast.success("נוסף לעגלה", `${product.title} נוסף לעגלת הקניות`);
  };

  const handleToggleWishlist = async () => {
    if (!product) return;
    
    const wasLiked = isLiked;
    const added = await toggleItem(product);
    
    if (added && !wasLiked) {
      setShowHeartParticles(true);
      setTimeout(() => setShowHeartParticles(false), 1000);
      alertToast.success('המוצר נוסף למועדפים!');
    } else if (wasLiked) {
      alertToast.info('המוצר הוסר מהמועדפים');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" dir="rtl">
        <Header />
        <div className="container mx-auto px-4 py-24 flex flex-col items-center justify-center gap-4">
          <div className="gold-bar-loader" />
        </div>
        <Footer2 />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen" dir="rtl">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl mb-4">המוצר לא נמצא</h1>
            <Link to="/shop">
              <Button>חזרה לחנות</Button>
            </Link>
          </div>
        </div>
        <Footer2 />
      </div>
    );
  }

  const images = product.images.edges;
  const currentImage = images[selectedImage]?.node;
  // Always use the gallery-selected image (currentImage), not the variant image
  const displayedImageUrl = currentImage?.url;
  const displayedImageAlt = currentImage?.altText || product.title;

  // Prepare SEO data
  const productPrice = selectedVariant?.price?.amount || product.priceRange.minVariantPrice.amount;
  const productCurrency = selectedVariant?.price?.currencyCode || product.priceRange.minVariantPrice.currencyCode;
  const productImage = images[0]?.node?.url || '';
  const productDescription = product.description?.slice(0, 160) || `${product.title} - תכשיט יוקרתי מבית Alice Jewellery`;
  // Use collection from URL for SEO, falling back to referrer or product's collection
  const categoryHandle = collection || referrerCategory?.id || product?.collections?.edges[0]?.node?.handle || '';
  const categoryName = (collection && product?.collections?.edges.find(c => c.node.handle === collection)?.node.title) 
    || referrerCategory?.title 
    || product?.collections?.edges[0]?.node?.title 
    || 'תכשיטים';
  // Canonical URL should include collection for Shopify compatibility
  const productUrl = categoryHandle 
    ? `${SITE_URL}/collections/${categoryHandle}/products/${product.handle}`
    : `${SITE_URL}/products/${product.handle}`;

  return (
    <PageTransition>
      <SEO
        title={`${product.title} | Alice Jewellery`}
        description={productDescription}
        canonical={productUrl}
        ogImage={productImage}
        ogType="product"
        product={{
          name: product.title,
          description: productDescription,
          image: productImage,
          price: productPrice,
          currency: productCurrency,
          availability: selectedVariant?.availableForSale !== false ? 'InStock' : 'OutOfStock',
          sku: selectedVariant?.sku || product.handle,
          brand: 'Alice Jewellery',
          url: productUrl,
        }}
        breadcrumbs={[
          { name: 'ראשי', url: '/' },
          ...(categoryHandle ? [{ name: categoryName, url: `/collections/${categoryHandle}` }] : []),
          { name: product.title, url: categoryHandle ? `/collections/${categoryHandle}/products/${product.handle}` : `/products/${product.handle}` },
        ]}
      />
      <div className="min-h-screen" dir="rtl">
        <Header />
      
      <main className="container mx-auto px-[30px] md:px-[50px] lg:px-[150px] py-8">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 animate-fade-in">
          <Link to="/" className="hover:text-foreground transition-colors">
            ראשי
          </Link>
          <ChevronRight className="h-4 w-4 rotate-180" />
          {/* Show collection from URL first, then referrer, then product's first collection */}
          {collection ? (
            <>
              <Link 
                to={`/collections/${collection}`}
                className="hover:text-foreground transition-colors"
              >
                {product?.collections?.edges.find(c => c.node.handle === collection)?.node.title || collection}
              </Link>
              <ChevronRight className="h-4 w-4 rotate-180" />
            </>
          ) : referrerCategory ? (
            <>
              <Link 
                to={`/collections/${referrerCategory.id}`}
                className="hover:text-foreground transition-colors"
              >
                {referrerCategory.title}
              </Link>
              <ChevronRight className="h-4 w-4 rotate-180" />
            </>
          ) : product?.collections?.edges[0] ? (
            <>
              <Link 
                to={`/collections/${product.collections.edges[0].node.handle}`}
                className="hover:text-foreground transition-colors"
              >
                {product.collections.edges[0].node.title}
              </Link>
              <ChevronRight className="h-4 w-4 rotate-180" />
            </>
          ) : null}
          <span className="text-foreground">{product.title}</span>
        </div>

        <div className="w-full h-px bg-border mb-8"></div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 pb-8 mb-8 border-b">
          {/* Image Gallery - Sticky */}
          <div className="animate-fade-in" style={{ animationDelay: '0.1s', animationFillMode: 'backwards' }}>
            <div className="md:sticky md:top-24 space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-secondary/20 rounded-lg overflow-hidden relative">
                <AnimatePresence mode="wait">
                  {displayedImageUrl && (
                    <motion.div
                      key={displayedImageUrl}
                      className="w-full h-full absolute inset-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <ImageMagnifier
                        src={displayedImageUrl}
                        alt={displayedImageAlt}
                        magnifierSize={180}
                        zoomLevel={2.5}
                        className="w-full h-full"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedImage(index);
                        
                        // Extract base URL without query params for comparison
                        const getBaseUrl = (url: string) => url.split('?')[0];
                        const clickedBaseUrl = getBaseUrl(image.node.url);
                        
                        // Find variant that has this image and sync selection
                        const matchingVariant = product?.variants.edges.find(
                          (v) => v.node.image?.url && getBaseUrl(v.node.image.url) === clickedBaseUrl
                        );
                        
                        if (matchingVariant) {
                          setSelectedVariant(matchingVariant.node);
                          // Update selected options to match this variant
                          const newOptions: Record<string, string> = {};
                          matchingVariant.node.selectedOptions.forEach((opt) => {
                            newOptions[opt.name] = opt.value;
                          });
                          setSelectedOptions(newOptions);
                        }
                      }}
                      className={`aspect-square bg-secondary/20 rounded-md overflow-hidden border-2 transition-all ${
                        selectedImage === index
                          ? "border-primary"
                          : "border-transparent hover:border-primary/50"
                      }`}
                    >
                      <img
                        src={image.node.url}
                        alt={image.node.altText || `${product.title} ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'backwards' }}>
            <div className="relative">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-3xl" style={{ fontWeight: 300 }}>
                    {product.title}
                  </h1>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {/* Share Button */}
                  <motion.button
                    onClick={async () => {
                      const shareUrl = window.location.href;
                      const shareTitle = product.title;
                      const shareText = `${product.title} - ${formatPrice(selectedVariant?.price?.amount || product.priceRange.minVariantPrice.amount, selectedVariant?.price?.currencyCode || product.priceRange.minVariantPrice.currencyCode)}`;
                      
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            title: shareTitle,
                            text: shareText,
                            url: shareUrl,
                          });
                        } catch (err) {
                          // User cancelled or error
                        }
                      } else {
                        // Fallback: copy to clipboard
                        await navigator.clipboard.writeText(shareUrl);
                        alertToast.success('הקישור הועתק ללוח!');
                      }
                    }}
                    className="bg-background/80 hover:bg-background p-3 rounded-full transition-all duration-300 border border-border/50"
                    whileTap={{ scale: 0.9 }}
                    aria-label="שתף מוצר"
                  >
                    <Share2 className="h-6 w-6 text-foreground/60 hover:text-primary transition-colors" />
                  </motion.button>

                  {/* Wishlist Heart Button */}
                  <div className="relative">
                    <motion.button
                      onClick={handleToggleWishlist}
                      className="bg-background/80 hover:bg-background p-3 rounded-full transition-all duration-300 border border-border/50"
                      whileTap={{ scale: 0.8 }}
                      animate={isLiked ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 0.3 }}
                      aria-label={isLiked ? 'הסר מהמועדפים' : 'הוסף למועדפים'}
                    >
                      <motion.div
                        animate={isLiked ? { 
                          scale: [1, 1.2, 1],
                          rotate: [0, -10, 10, -10, 0]
                        } : {}}
                        transition={{ duration: 0.5 }}
                      >
                        <Heart 
                          className={`h-6 w-6 transition-all duration-300 ${
                            isLiked 
                              ? 'fill-red-500 text-red-500' 
                              : 'text-foreground/60 hover:text-red-500'
                          }`}
                        />
                      </motion.div>
                    </motion.button>

                    {/* Heart Particles Animation */}
                    <AnimatePresence>
                      {showHeartParticles && (
                        <>
                          {[...Array(6)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute top-3 right-3 pointer-events-none"
                              initial={{ 
                                scale: 0.5,
                                opacity: 1,
                                x: 0,
                                y: 0
                              }}
                              animate={{
                                scale: 0,
                                opacity: 0,
                                x: Math.cos((i * Math.PI * 2) / 6) * 50,
                                y: Math.sin((i * Math.PI * 2) / 6) * 50,
                              }}
                              exit={{ opacity: 0 }}
                              transition={{
                                duration: 0.8,
                                ease: "easeOut"
                              }}
                            >
                              <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                            </motion.div>
                          ))}
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>

            {(product.descriptionHtml || product.description) && (
              <>
                <div className="w-full h-px bg-border"></div>
                {product.descriptionHtml ? (
                  <div 
                    className="text-muted-foreground leading-relaxed prose prose-sm max-w-none rtl:text-right [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pr-5 [&_ol]:list-decimal [&_ol]:pr-5 [&_li]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_a]:text-primary [&_a]:underline"
                    style={{ fontWeight: 300 }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.descriptionHtml) }}
                  />
                ) : (
                  <div className="text-muted-foreground leading-relaxed" style={{ fontWeight: 300 }}>
                    {product.description}
                  </div>
                )}
              </>
            )}

            {/* Ring Size Guide Link */}
            {hasRingSizeGuideTag(product.tags) && (
              <div className="py-2">
                <RingSizeGuideDialog />
              </div>
            )}

            {product.options && product.options.length > 0 && (
              <>
                <div className="w-full h-px bg-border"></div>
                {/* Options */}
                <div className="space-y-4">
                  {product.options
                    .filter((option) => {
                      // Hide "מילים" option since it's auto-selected based on engraving word count
                      const optionNameLower = option.name.toLowerCase();
                      return optionNameLower !== 'מילים' && optionNameLower !== 'words';
                    })
                    .map((option) => {
                    // Check if this option is a color option
                    const isColorOption = isColorOptionName(option.name);
                    
                    return (
                      <div key={option.name}>
                        <label className="block text-sm mb-2" style={{ fontWeight: 300 }}>
                          {option.name}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {option.values.map((value) => {
                            // Use static color map for color options
                            const colorValue = isColorOption ? getColorHexSync(value) : undefined;
                            const isWhiteColor = colorValue?.toUpperCase() === '#FFFFFF' || 
                                                colorValue?.toUpperCase() === '#E8E8E8' ||
                                                colorValue?.toUpperCase() === '#E5E4E2';
                            
                            if (isColorOption) {
                              return (
                                <TooltipProvider key={value} delayDuration={100}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => handleOptionChange(option.name, value)}
                                        className={`w-10 h-10 rounded-full border-2 transition-all relative overflow-hidden ${
                                          selectedOptions[option.name] === value
                                            ? "border-primary ring-2 ring-primary ring-offset-2"
                                            : "border-border hover:border-primary/50"
                                        }`}
                                        style={{ background: getJewelryGradient(colorValue || '#D4AF37') }}
                                      >
                                        {isWhiteColor && (
                                          <span className="absolute inset-0 rounded-full border border-muted" />
                                        )}
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="bg-foreground text-background text-xs">
                                      {value}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }
                            
                            return (
                              <button
                                key={value}
                                onClick={() => handleOptionChange(option.name, value)}
                                className={`px-4 py-2 rounded-md border transition-all ${
                                  selectedOptions[option.name] === value
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:border-primary/50"
                                }`}
                                style={{ fontWeight: 300 }}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Engraving Options - Only show for products with engraving tag */}
            {requiresEngraving && (
              <>
                <div className="w-full h-px bg-border"></div>
                
                {/* No Engraving Checkbox - Only show if product has "מילים" option */}
                {hasWordsOption && (
                  <div className="flex items-center gap-3 py-2">
                    <Checkbox
                      id="no-engraving"
                      checked={noEngraving}
                      onCheckedChange={(checked) => {
                        setNoEngraving(checked === true);
                        if (checked) {
                          // Clear engraving data and errors when selecting no engraving
                          setEngravingData(null);
                          setEngravingErrors({});
                        }
                      }}
                    />
                    <label
                      htmlFor="no-engraving"
                      className="text-sm cursor-pointer select-none"
                      style={{ fontWeight: 300 }}
                    >
                      ללא חריטה
                    </label>
                  </div>
                )}
                
                {/* Only show engraving options if not disabled */}
                {!noEngraving && (
                  <EngravingOptions
                    value={engravingData}
                    onChange={setEngravingData}
                    price={ENGRAVING_PRICE}
                    currencyCode={selectedVariant?.price.currencyCode || product.priceRange.minVariantPrice.currencyCode}
                    engravingCount={engravingCount}
                    errors={engravingErrors}
                  />
                )}
              </>
            )}

            {/* Icon Selection - Only show for products with icon tag and when engraving is enabled */}
            {hasIcons && !noEngraving && (
              <>
                <div className="w-full h-px bg-border"></div>
                <IconSelection
                  value={iconData}
                  onChange={setIconData}
                />
              </>
            )}

            <div className="w-full h-px bg-border"></div>

            {/* Quantity and Price */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <label className="block text-sm mb-2" style={{ fontWeight: 300 }}>
                  כמות
                </label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-lg w-12 text-center" style={{ fontWeight: 300 }}>
                    {quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Dynamic Price - includes engraving if applicable */}
              <div className="text-left">
              <span className="block text-sm text-muted-foreground mb-2" style={{ fontWeight: 300 }}>
                  סה"כ
                </span>
                {(() => {
                  const basePrice = selectedVariant
                    ? parseFloat(selectedVariant.price.amount)
                    : parseFloat(product.priceRange.minVariantPrice.amount);
                  const iconCount = (hasIcons && iconData && !noEngraving) ? iconData.iconIds.length : 0;
                  const iconsPrice = iconCount * ICON_PRICE;
                  const totalPrice = (basePrice + iconsPrice) * quantity;
                  const currencySymbol = selectedVariant?.price.currencyCode === 'ILS' || product.priceRange.minVariantPrice.currencyCode === 'ILS' ? '₪' : '$';
                  
                  return (
                    <>
                      <div className="text-2xl text-primary flex items-center gap-1" style={{ fontWeight: 300, direction: 'ltr' }}>
                        <span>{currencySymbol}</span>
                        <SlidingNumber value={Math.round(totalPrice)} />
                      </div>
                      {iconCount > 0 && (
                        <div className="text-xs text-muted-foreground mt-1" style={{ fontWeight: 300 }}>
                          כולל {iconCount} {iconCount === 1 ? 'סמל' : 'סמלים'} (+₪{iconsPrice})
                        </div>
                      )}
                      {quantity > 1 && (
                        <div className="text-sm text-muted-foreground mt-1" style={{ fontWeight: 300 }}>
                          <span className="block" style={{ direction: 'ltr' }}>
                            {currencySymbol}
                            {Math.round(basePrice + iconsPrice).toLocaleString('he-IL')} ליחידה
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              size="lg"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleAddToCart}
              disabled={!selectedVariant || !selectedVariant.availableForSale}
              style={{ fontWeight: 300 }}
            >
              {selectedVariant?.availableForSale ? "הוסף לעגלה" : "אזל מהמלאי"}
            </Button>

            {/* Additional Info */}
            <div className="pt-6 border-t space-y-3 text-sm text-muted-foreground" style={{ fontWeight: 300 }}>
              <div className="flex justify-between">
                <span>משלוח חינם</span>
                <span>בהזמנה מעל ₪500</span>
              </div>
              <div className="flex justify-between">
                <span>אחריות</span>
                <span>אחריות יצרן מלאה</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products / Recently Viewed Tabs */}
        {(relatedProducts.length > 0 || recentlyViewedProducts.length > 0) && (
          <section className="mt-16 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'backwards' }}>
            {/* Tab Container with subtle frame */}
            <div className="border border-border/60 rounded-xl p-6 md:p-8 bg-secondary/10 shadow-sm">
              {/* Tab Headers */}
              <div className="flex justify-center mb-8">
                <div className="inline-flex items-center gap-1 bg-background border border-border/60 rounded-full p-1 shadow-sm">
                  <button
                    onClick={() => setActiveProductsTab('related')}
                    className={`px-5 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm transition-all duration-300 ${
                      activeProductsTab === 'related'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                    }`}
                    style={{ fontWeight: 400 }}
                  >
                    מוצרים שעשויים לעניין אותך
                  </button>
                  <button
                    onClick={() => setActiveProductsTab('recent')}
                    className={`px-5 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm transition-all duration-300 ${
                      activeProductsTab === 'recent'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                    }`}
                    style={{ fontWeight: 400 }}
                  >
                    נצפו לאחרונה
                  </button>
                </div>
              </div>
              
              {/* Tab Content */}
              <AnimatePresence mode="wait">
                {activeProductsTab === 'related' && relatedProducts.length > 0 && (
                  <motion.div
                    key="related"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
                  >
                    {relatedProducts.map((product) => (
                      <ShopifyProductCard key={product.node.id} product={product.node} collectionHandle={collection} />
                    ))}
                  </motion.div>
                )}
                
                {activeProductsTab === 'related' && relatedProducts.length === 0 && (
                  <motion.div
                    key="related-empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-12 text-muted-foreground"
                    style={{ fontWeight: 300 }}
                  >
                    אין מוצרים קשורים להצגה
                  </motion.div>
                )}
                
                {activeProductsTab === 'recent' && recentlyViewedProducts.length > 0 && (
                  <motion.div
                    key="recent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
                  >
                    {recentlyViewedProducts.map((item) => (
                      <ShopifyProductCard key={item.id} product={item} collectionHandle={collection} />
                    ))}
                  </motion.div>
                )}
                
                {activeProductsTab === 'recent' && recentlyViewedProducts.length === 0 && (
                  <motion.div
                    key="recent-empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-12 text-muted-foreground"
                    style={{ fontWeight: 300 }}
                  >
                    לא צפית עדיין במוצרים נוספים
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </section>
        )}

        {/* Product FAQ Section */}
        <ProductFAQ />
      </main>

      <Footer2 />
    </div>
    </PageTransition>
  );
};

export default Product;
